import React, { useEffect, useState } from "react";
import axios from "axios";
import toast, { Toaster } from "react-hot-toast";
import Sidebar from "../components/MasterSideBar";
import Topbar from "../components/TopbarNav";
import Button from "../components/Button";
import KPIForm from "../components/performance/KPIForm";
import KeyPerformanceIndicators from "../components/performance/KeyPerformanceIndicators";
import ConfirmModal from "../components/performance/ConfirmModal";
import CapacityActivityModal from "../pages/hrDashboard/capacityActivityModal";
import CapacityActivityTable from "../components/performance/CapacityActivityTable";
import { Target, TrendingUp, AlertTriangle, XCircle, Briefcase, Users } from "lucide-react";
import WorkPlanUploadModal from '../components/workplan';

const Performance = () => {
  const [activeTab, setActiveTab] = useState('kpi'); // 'kpi' or 'capacity'
  const [kpis, setKpis] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedKpi, setSelectedKpi] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const [capacityActivities, setCapacityActivities] = useState([]);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [showCapacityActivityModal, setShowCapacityActivityModal] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  // KPI search and pagination states
  const [kpiSearchTerm, setKpiSearchTerm] = useState("");
  const [kpiCardCurrentPage, setKpiCardCurrentPage] = useState(1);
  const [kpiTableCurrentPage, setKpiTableCurrentPage] = useState(1);
  const kpiCardItemsPerPage = 6; // Number of KPI cards per page
  const kpiTableItemsPerPage = 10; // Number of KPI table rows per page
  const [showWorkPlanModal, setShowWorkPlanModal] = useState(false); // Workplan modal state
  // View mode state
  const [viewMode, setViewMode] = useState('card'); // 'card' or 'table'

  // Track newly created KPI ID
  const [newKpiId, setNewKpiId] = useState(null);

  // Filter states
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'on-track', 'at-risk', 'behind'
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [timeframeFilter, setTimeframeFilter] = useState('all'); // 'all', 'this-quarter', 'this-year'

  // Function to extract timestamp from MongoDB ObjectId
  const getTimestampFromObjectId = (id) => {
    if (!id) return 0;
    // MongoDB ObjectId contains a 4-byte timestamp in the first 8 characters
    return parseInt(id.substring(0, 8), 16) * 1000;
  };

  // Function to get the most recent timestamp from a KPI object
  const getKpiTimestamp = (kpi) => {
    // First try to use createdAt if available
    if (kpi.createdAt) {
      return new Date(kpi.createdAt).getTime();
    }
    // Fall back to ObjectId timestamp
    return getTimestampFromObjectId(kpi._id);
  };

  // Fetch KPIs
  const fetchKpis = async () => {
    try {
      const res = await axios.get(
        "https://ims.holdthechild.net/api/kpis"
      );

      // Sort KPIs by creation date (newest first)
      const sortedKpis = [...res.data].sort((a, b) => {
        const timestampA = getKpiTimestamp(a);
        const timestampB = getKpiTimestamp(b);

        // Log for debugging
        console.log(`Comparing KPIs: ${b.title} (${new Date(timestampB).toISOString()}) vs ${a.title} (${new Date(timestampA).toISOString()})`);

        return timestampB - timestampA; // Descending order (newest first)
      });

      console.log("Sorted KPIs:", sortedKpis.map(k => ({ title: k.title, timestamp: new Date(getKpiTimestamp(k)).toISOString() })));

      setKpis(sortedKpis);
    } catch (err) {
      toast.error("Failed to load KPIs");
    }
  };

  // Fetch Capacity Activities with Pagination
  const fetchCapacityActivities = async (page = 1) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Unauthorized. Please login again.");
        window.location.href = "/login";
        return;
      }

      const res = await axios.get(
        "https://ims.holdthechild.net/api/capacity-activities?page=${page}&limit=10",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const { data, totalPages } = res.data;
      // Sort activities by creation date (newest first)
      const sortedActivities = [...data].sort((a, b) => {
        const dateA = new Date(a.createdAt || 0).getTime();
        const dateB = new Date(b.createdAt || 0).getTime();
        return dateB - dateA; // Descending order (newest first)
      });
      setCapacityActivities(sortedActivities);
      setTotalPages(totalPages || 1);
      setCurrentPage(page);
    } catch (error) {
      toast.error("Failed to fetch capacity activities");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKpis();
    fetchCapacityActivities(); // initial page 1
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("token");
    window.location.href = "/login";
  };

  // Calculate KPI status based on progress
  const calculateKpiStatus = (kpi) => {
    if (!kpi.targetValue || kpi.targetValue === 0) return 'no-target';

    const progress = (kpi.currentValue || 0) / kpi.targetValue;
    const now = new Date();
    const createdAt = new Date(kpi.createdAt || now);
    const daysSinceCreation = Math.floor((now - createdAt) / (1000 * 60 * 60 * 24));

    // For KPIs less than 30 days old, we expect less progress
    const expectedProgress = Math.min(1, daysSinceCreation / 90); // Assuming 90-day cycle

    if (progress >= expectedProgress) return 'on-track';
    if (progress >= expectedProgress * 0.7) return 'at-risk';
    return 'behind';
  };

  // Filter KPIs based on search term and filters
  const filteredKpis = [...kpis]
    .filter(kpi => {
      // Search filter
      const matchesSearch =
        kpi.title.toLowerCase().includes(kpiSearchTerm.toLowerCase()) ||
        kpi.description.toLowerCase().includes(kpiSearchTerm.toLowerCase()) ||
        (kpi.staff && kpi.staff.toLowerCase().includes(kpiSearchTerm.toLowerCase()));

      // Status filter
      const status = calculateKpiStatus(kpi);
      const matchesStatus = statusFilter === 'all' || status === statusFilter;

      // Department filter (if we had department field)
      const matchesDepartment = departmentFilter === 'all' ||
        (kpi.department && kpi.department === departmentFilter);

      // Timeframe filter
      const now = new Date();
      const createdAt = new Date(kpi.createdAt || now);
      const daysSinceCreation = Math.floor((now - createdAt) / (1000 * 60 * 60 * 24));

      let matchesTimeframe = true;
      if (timeframeFilter === 'this-quarter') {
        matchesTimeframe = daysSinceCreation <= 90;
      } else if (timeframeFilter === 'this-year') {
        matchesTimeframe = daysSinceCreation <= 365;
      }

      return matchesSearch && matchesStatus && matchesDepartment && matchesTimeframe;
    })
    .sort((a, b) => {
      // Sort by status first (on-track first, then at-risk, then behind)
      const statusA = calculateKpiStatus(a);
      const statusB = calculateKpiStatus(b);

      const statusOrder = { 'on-track': 1, 'at-risk': 2, 'behind': 3, 'no-target': 4 };
      if (statusOrder[statusA] !== statusOrder[statusB]) {
        return statusOrder[statusA] - statusOrder[statusB];
      }

      // Then sort by creation date (newest first)
      const timestampA = getKpiTimestamp(a);
      const timestampB = getKpiTimestamp(b);
      return timestampB - timestampA;
    });

  // Calculate pagination for KPI Card View
  const indexOfLastCardKpi = kpiCardCurrentPage * kpiCardItemsPerPage;
  const indexOfFirstCardKpi = indexOfLastCardKpi - kpiCardItemsPerPage;
  const currentCardKpis = filteredKpis.slice(indexOfFirstCardKpi, indexOfLastCardKpi);
  const kpiCardTotalPages = Math.ceil(filteredKpis.length / kpiCardItemsPerPage);

  // Calculate pagination for KPI Table View
  const indexOfLastTableKpi = kpiTableCurrentPage * kpiTableItemsPerPage;
  const indexOfFirstTableKpi = indexOfLastTableKpi - kpiTableItemsPerPage;
  const currentTableKpis = filteredKpis.slice(indexOfFirstTableKpi, indexOfLastTableKpi);
  const kpiTableTotalPages = Math.ceil(filteredKpis.length / kpiTableItemsPerPage);

  // Handle KPI Card pagination
  const handleKpiCardPageChange = (pageNumber) => {
    setKpiCardCurrentPage(pageNumber);
  };

  // Handle KPI Table pagination
  const handleKpiTablePageChange = (pageNumber) => {
    setKpiTableCurrentPage(pageNumber);
  };

  // Reset pagination when filters change
  useEffect(() => {
    setKpiCardCurrentPage(1);
    setKpiTableCurrentPage(1);
  }, [kpiSearchTerm, statusFilter, departmentFilter, timeframeFilter]);

  // Clear new KPI highlight after 5 seconds
  useEffect(() => {
    if (newKpiId) {
      const timer = setTimeout(() => {
        setNewKpiId(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [newKpiId]);

  // Calculate KPI statistics
  const calculateStats = () => {
    const stats = {
      total: kpis.length,
      onTrack: 0,
      atRisk: 0,
      behind: 0,
      noTarget: 0
    };

    kpis.forEach(kpi => {
      const status = calculateKpiStatus(kpi);
      if (status === 'on-track') stats.onTrack++;
      else if (status === 'at-risk') stats.atRisk++;
      else if (status === 'behind') stats.behind++;
      else stats.noTarget++;
    });

    return stats;
  };

  const kpiStats = calculateStats();

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Toaster />

      {/* Sidebar */}
      <Sidebar onLogout={handleLogout} />


      {/* Main Content */}
      <div className="flex-1 md:ml-64">
        <Topbar />

        <div className="p-4 sm:p-6 mt-16 space-y-6">
          {/* Header Section */}
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Performance Management</h1>
            <p className="text-gray-600">Track KPIs and manage capacity building activities</p>
          </div>

          {/* KPI Statistics Overview */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 transition-all hover:shadow-md">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">Total KPIs</p>
                  <div className="flex items-baseline">
                    <span className="text-3xl font-bold text-gray-900">{kpiStats.total}</span>
                    <span className="ml-2 text-sm font-medium text-gray-500">Active</span>
                  </div>
                </div>
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Target className="h-6 w-6 text-blue-900" />
                </div>
              </div>
              <div className="mt-4">
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-900 rounded-full"
                    style={{ width: `${Math.min(100, (kpiStats.total / Math.max(1, kpiStats.total)) * 100)}%` }}
                  ></div>
                </div>
                <p className="mt-2 text-xs text-gray-500">All active performance indicators</p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 transition-all hover:shadow-md">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">On Track</p>
                  <div className="flex items-baseline">
                    <span className="text-3xl font-bold text-green-600">{kpiStats.onTrack}</span>
                    <span className="ml-2 text-sm font-medium text-gray-500">
                      {kpiStats.total > 0 ? `${Math.round((kpiStats.onTrack / kpiStats.total) * 100)}%` : '0%'}
                    </span>
                  </div>
                </div>
                <div className="p-3 bg-green-100 rounded-lg">
                  <TrendingUp className="h-6 w-6 text-green-600" />
                </div>
              </div>
              <div className="mt-4">
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-green-600 rounded-full"
                    style={{ width: `${kpiStats.total > 0 ? (kpiStats.onTrack / kpiStats.total) * 100 : 0}%` }}
                  ></div>
                </div>
                <p className="mt-2 text-xs text-gray-500">Meeting expected progress</p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 transition-all hover:shadow-md">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">At Risk</p>
                  <div className="flex items-baseline">
                    <span className="text-3xl font-bold text-yellow-600">{kpiStats.atRisk}</span>
                    <span className="ml-2 text-sm font-medium text-gray-500">
                      {kpiStats.total > 0 ? `${Math.round((kpiStats.atRisk / kpiStats.total) * 100)}%` : '0%'}
                    </span>
                  </div>
                </div>
                <div className="p-3 bg-yellow-100 rounded-lg">
                  <AlertTriangle className="h-6 w-6 text-yellow-600" />
                </div>
              </div>
              <div className="mt-4">
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-yellow-600 rounded-full"
                    style={{ width: `${kpiStats.total > 0 ? (kpiStats.atRisk / kpiStats.total) * 100 : 0}%` }}
                  ></div>
                </div>
                <p className="mt-2 text-xs text-gray-500">Needs attention to stay on track</p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 transition-all hover:shadow-md">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">Behind</p>
                  <div className="flex items-baseline">
                    <span className="text-3xl font-bold text-red-600">{kpiStats.behind}</span>
                    <span className="ml-2 text-sm font-medium text-gray-500">
                      {kpiStats.total > 0 ? `${Math.round((kpiStats.behind / kpiStats.total) * 100)}%` : '0%'}
                    </span>
                  </div>
                </div>
                <div className="p-3 bg-red-100 rounded-lg">
                  <XCircle className="h-6 w-6 text-red-600" />
                </div>
              </div>
              <div className="mt-4">
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-red-600 rounded-full"
                    style={{ width: `${kpiStats.total > 0 ? (kpiStats.behind / kpiStats.total) * 100 : 0}%` }}
                  ></div>
                </div>
                <p className="mt-2 text-xs text-gray-500">Requires immediate intervention</p>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="bg-white rounded-xl shadow-sm p-1 mb-6">
            <div className="flex">
              <button
                className={`flex items-center gap-2 py-3 px-6 rounded-lg font-medium text-sm transition-all ${activeTab === 'kpi' ? 'bg-orange-100 text-orange-600 shadow-sm' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
                onClick={() => setActiveTab('kpi')}
              >
                <Target className="w-4 h-4" />
                KPIs
              </button>
              <button
                className={`flex items-center gap-2 py-3 px-6 rounded-lg font-medium text-sm transition-all ${activeTab === 'capacity' ? 'bg-orange-100 text-orange-600 shadow-sm' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
                onClick={() => setActiveTab('capacity')}
              >
                <Users className="w-4 h-4" />
                Capacity Building
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            {activeTab === 'kpi' ? (
              <div className="p-6">
                {/* Action Buttons */}
                <div className="flex flex-wrap gap-4 mb-6">
                  <Button
                    onClick={() => {
                      setSelectedKpi(null);
                      setShowForm(true);
                    }}
                  >
                    Add KPI
                  </Button>
                  <Button onClick={() => setShowWorkPlanModal(true)}>
                    Upload Workplan
                  </Button>
                  <Button>View Development Goal</Button>
                </div>

                {/* KPI Section with View Toggle */}
                <div className="mb-6">
                  <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 space-y-4 md:space-y-0">
                    <h3 className="font-semibold text-xl">Key Performance Indicators</h3>

                    <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                      {/* View Toggle */}
                      <div className="flex items-center bg-gray-100 rounded-lg p-1">
                        <button
                          onClick={() => setViewMode('card')}
                          className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${viewMode === 'card'
                            ? 'bg-white shadow text-orange-600'
                            : 'text-gray-600 hover:text-gray-900'
                            }`}
                        >
                          Card View
                        </button>
                        <button
                          onClick={() => setViewMode('table')}
                          className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${viewMode === 'table'
                            ? 'bg-white shadow text-orange-600'
                            : 'text-gray-600 hover:text-gray-900'
                            }`}
                        >
                          Table View
                        </button>
                      </div>

                      {/* Search */}
                      <div className="relative w-full sm:w-64">
                        <input
                          type="text"
                          placeholder="Search KPIs..."
                          value={kpiSearchTerm}
                          onChange={(e) => setKpiSearchTerm(e.target.value)}
                          className="w-full p-2 pl-10 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        />
                        <svg
                          className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Filters */}
                  <div className="flex flex-wrap gap-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm rounded-md"
                      >
                        <option value="all">All Statuses</option>
                        <option value="on-track">On Track</option>
                        <option value="at-risk">At Risk</option>
                        <option value="behind">Behind</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Timeframe</label>
                      <select
                        value={timeframeFilter}
                        onChange={(e) => setTimeframeFilter(e.target.value)}
                        className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm rounded-md"
                      >
                        <option value="all">All Time</option>
                        <option value="this-quarter">This Quarter</option>
                        <option value="this-year">This Year</option>
                      </select>
                    </div>
                  </div>

                  {/* KPI Content */}
                  {filteredKpis.length === 0 ? (
                    <div className="text-center py-10">
                      <p className="text-gray-500">No KPIs found matching your filters.</p>
                    </div>
                  ) : (
                    <>
                      {viewMode === 'card' ? (
                        // Card View
                        <>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {currentCardKpis.map((kpi) => {
                              const status = calculateKpiStatus(kpi);
                              const statusColors = {
                                'on-track': 'border-green-500 bg-green-50',
                                'at-risk': 'border-yellow-500 bg-yellow-50',
                                'behind': 'border-red-500 bg-red-50',
                                'no-target': 'border-gray-500 bg-gray-50'
                              };

                              return (
                                <div
                                  key={kpi._id}
                                  className={`border rounded-lg p-4 hover:shadow-md transition-shadow duration-300 ${newKpiId === kpi._id ? 'border-orange-500 border-2 bg-orange-50' : statusColors[status]
                                    }`}
                                >
                                  <div className="flex justify-between items-start">
                                    <h4 className="font-semibold text-lg text-gray-800">{kpi.title}</h4>
                                    <div className="flex space-x-2">
                                      <button
                                        onClick={() => {
                                          setSelectedKpi(kpi);
                                          setShowForm(true);
                                        }}
                                        className="text-blue-900 hover:text-blue-800"
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                        </svg>
                                      </button>
                                      <button
                                        onClick={() => setConfirmDelete(kpi._id)}
                                        className="text-red-600 hover:text-red-800"
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                          <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                        </svg>
                                      </button>
                                    </div>
                                  </div>

                                  <div className="mt-2 flex items-center">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status === 'on-track' ? 'bg-green-100 text-green-800' :
                                      status === 'at-risk' ? 'bg-yellow-100 text-yellow-800' :
                                        status === 'behind' ? 'bg-red-100 text-red-800' :
                                          'bg-gray-100 text-gray-800'
                                      }`}>
                                      {status === 'on-track' ? 'On Track' :
                                        status === 'at-risk' ? 'At Risk' :
                                          status === 'behind' ? 'Behind' : 'No Target'}
                                    </span>
                                    {kpi.staff && (
                                      <span className="ml-2 text-xs text-gray-500">
                                        {kpi.staff}
                                      </span>
                                    )}
                                  </div>

                                  <p className="text-gray-600 mt-2 text-sm">{kpi.description}</p>

                                  <div className="mt-4">
                                    <div className="flex justify-between text-sm text-gray-500 mb-1">
                                      <span>Progress</span>
                                      <span>{kpi.currentValue || 0}/{kpi.targetValue}</span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                      <div
                                        className={`h-2 rounded-full ${status === 'on-track' ? 'bg-green-600' :
                                          status === 'at-risk' ? 'bg-yellow-600' :
                                            status === 'behind' ? 'bg-red-600' : 'bg-gray-600'
                                          }`}
                                        style={{ width: `${Math.min(100, ((kpi.currentValue || 0) / (kpi.targetValue || 1)) * 100)}%` }}
                                      ></div>
                                    </div>
                                  </div>

                                  {newKpiId === kpi._id && (
                                    <div className="mt-3 text-xs text-orange-600 font-medium flex items-center">
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                      </svg>
                                      New
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>

                          {/* KPI Pagination for Card View */}
                          {kpiCardTotalPages > 1 && (
                            <div className="flex justify-center items-center mt-8 space-x-2">
                              <button
                                onClick={() => handleKpiCardPageChange(kpiCardCurrentPage - 1)}
                                disabled={kpiCardCurrentPage === 1}
                                className={`px-3 py-1 rounded-md ${kpiCardCurrentPage === 1 ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                              >
                                Previous
                              </button>
                              <span className="text-gray-700">
                                Page {kpiCardCurrentPage} of {kpiCardTotalPages}
                              </span>
                              <button
                                onClick={() => handleKpiCardPageChange(kpiCardCurrentPage + 1)}
                                disabled={kpiCardCurrentPage === kpiCardTotalPages}
                                className={`px-3 py-1 rounded-md ${kpiCardCurrentPage === kpiCardTotalPages ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                              >
                                Next
                              </button>
                            </div>
                          )}
                        </>
                      ) : (
                        // Table View
                        <>
                          <KeyPerformanceIndicators
                            kpis={currentTableKpis}
                            newKpiId={newKpiId}
                            calculateKpiStatus={calculateKpiStatus}
                            onEdit={(kpi) => {
                              setSelectedKpi(kpi);
                              setShowForm(true);
                            }}
                            onDelete={(id) => setConfirmDelete(id)}
                          />

                          {/* KPI Pagination for Table View */}
                          {kpiTableTotalPages > 1 && (
                            <div className="flex justify-center items-center mt-6 space-x-2">
                              <button
                                onClick={() => handleKpiTablePageChange(kpiTableCurrentPage - 1)}
                                disabled={kpiTableCurrentPage === 1}
                                className={`px-3 py-1 rounded-md ${kpiTableCurrentPage === 1 ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                              >
                                Previous
                              </button>
                              <span className="text-gray-700">
                                Page {kpiTableCurrentPage} of {kpiTableTotalPages}
                              </span>
                              <button
                                onClick={() => handleKpiTablePageChange(kpiTableCurrentPage + 1)}
                                disabled={kpiTableCurrentPage === kpiTableTotalPages}
                                className={`px-3 py-1 rounded-md ${kpiTableCurrentPage === kpiTableTotalPages ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                              >
                                Next
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="p-6">
                {/* Capacity Building Section */}
                <div className="flex justify-between items-center mb-6">
                  <h3 className="font-semibold text-xl">Capacity Building Activities</h3>
                  <Button
                    onClick={() => {
                      setSelectedActivity(null);
                      setShowCapacityActivityModal(true);
                    }}
                  >
                    Add Capacity Building Activity
                  </Button>
                </div>

                <CapacityActivityTable
                  activities={capacityActivities}
                  currentPage={currentPage}
                  onEdit={(activity) => {
                    setSelectedActivity(activity);
                    setShowCapacityActivityModal(true);
                  }}
                />
              </div>
            )}
          </div>

        </div>
      </div>

      {/* KPI Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex justify-center items-center z-50">
          <KPIForm
            onClose={() => {
              setShowForm(false);
              setSelectedKpi(null);
            }}
            onSuccess={(newKpi) => {
              console.log("New KPI created:", newKpi);
              if (newKpi && newKpi._id) {
                setNewKpiId(newKpi._id);
                console.log("Setting newKpiId to:", newKpi._id);

                // Optimistically add the new KPI to the top of the list
                setKpis(prevKpis => {
                  console.log("Adding new KPI to top of list:", newKpi);
                  return [newKpi, ...prevKpis];
                });

                // Reset pagination to first page
                console.log("Resetting pagination to page 1");
                setKpiCardCurrentPage(1);
                setKpiTableCurrentPage(1);
                console.log("kpiTableCurrentPage after reset:", 1);
              }
              setShowForm(false);
              setSelectedKpi(null);

              // Then fetch the latest list to ensure consistency
              fetchKpis();
            }}
            initialData={selectedKpi || {}}
            isEditMode={!!selectedKpi}
          />
        </div>
      )}

      {/* Confirm Delete Modal */}
      {confirmDelete && (
        <ConfirmModal
          message="Are you sure you want to delete this KPI?"
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => {
            axios
              .delete(
                `"https://ims.holdthechild.net"/api/kpis/${confirmDelete}`
              )
              .then(() => {
                toast.success("KPI deleted");
                fetchKpis();
              })
              .catch(() => toast.error("Failed to delete KPI"));
            setConfirmDelete(null);
          }}
        />
      )}

      {/* Capacity Activity Modal */}
      {showCapacityActivityModal && (
        <CapacityActivityModal
          isOpen={showCapacityActivityModal}
          onClose={() => {
            setShowCapacityActivityModal(false);
            setSelectedActivity(null);
          }}
          onSuccess={() => fetchCapacityActivities(currentPage)}
          initialData={selectedActivity}
        />
      )}




      {/* Capacity Activity Modal */}
      {showCapacityActivityModal && (
        <CapacityActivityModal
          isOpen={showCapacityActivityModal}
          onClose={() => {
            setShowCapacityActivityModal(false);
            setSelectedActivity(null);
          }}
          onSuccess={() => fetchCapacityActivities(currentPage)}
          initialData={selectedActivity}
        />
      )}

      {/* Work Plan Modal */}
      {showWorkPlanModal && (
        <WorkPlanUploadModal
          isOpen={showWorkPlanModal}
          onClose={() => setShowWorkPlanModal(false)}
          onSuccess={() => {
            setShowWorkPlanModal(false);
            toast.success("Work plan uploaded successfully!");
          }}
        />
      )}
    </div>  // ← This closes the main container
  );  // ← This closes the return
};  // ← This closes the Performance component

export default Performance;
